import { useState, useMemo, useEffect, useCallback } from 'react'
import type { DateRange } from 'react-day-picker'
import { isFuture, format, parse } from 'date-fns'
import { CalendarIcon, Loader2 } from 'lucide-react'

import { useCampaignSelection } from '@/entities/campaign'
import { useGenerateReport, type GroupingType, type ReportData } from '@/entities/report'
import { CampaignSelector } from '@/components/features/CampaignSelector'
import { ChatReports } from '@/pages/reports-chat/ChatReports'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ReportsCharts, ReportsTable } from './ui'

function formatDate(date: Date, formatStr: string): string {
  return format(date, formatStr)
}

// Parse URL hash to get initial state
function parseHashParams(): { startDate?: string; endDate?: string; grouping?: GroupingType } {
  const hash = window.location.hash.slice(1) // Remove #
  if (!hash) return {}

  const params = new URLSearchParams(hash)
  const startDate = params.get('start') || undefined
  const endDate = params.get('end') || undefined
  const grouping = params.get('grouping') as GroupingType | undefined

  return { startDate, endDate, grouping }
}

// Update URL hash with current state
function updateHashParams(startDate?: string, endDate?: string, grouping?: string) {
  const params = new URLSearchParams()
  if (startDate) params.set('start', startDate)
  if (endDate) params.set('end', endDate)
  if (grouping) params.set('grouping', grouping)

  const hash = params.toString()
  if (hash) {
    window.history.replaceState(null, '', `#${hash}`)
  } else {
    window.history.replaceState(null, '', window.location.pathname)
  }
}

// Parse date string to Date object
function parseDateString(dateStr: string | undefined): Date | undefined {
  if (!dateStr) return undefined
  try {
    const parsed = parse(dateStr, 'yyyy-MM-dd', new Date())
    return isNaN(parsed.getTime()) ? undefined : parsed
  } catch {
    return undefined
  }
}

export function Reports() {
  const { selectedCampaign } = useCampaignSelection()
  if (selectedCampaign?.telephony === 'custom') {
    return <ChatReports />
  }
  return <VoiceReports />
}

function VoiceReports() {
  // Campaign state (persisted to session storage)
  const { campaignId, setCampaignId, campaigns, campaignsLoading } = useCampaignSelection()

  // Initialize state from URL hash
  const initialParams = useMemo(() => parseHashParams(), [])

  // Form state
  const [grouping, setGrouping] = useState<GroupingType | ''>(() => initialParams.grouping || '')
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const from = parseDateString(initialParams.startDate)
    const to = parseDateString(initialParams.endDate)
    if (from && to) {
      return { from, to }
    }
    return undefined
  })

  // Report state
  const [report, setReport] = useState<ReportData | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Generate report mutation
  const generateReport = useGenerateReport()

  // Update URL hash when date range or grouping changes
  useEffect(() => {
    const startDate = dateRange?.from ? formatDate(dateRange.from, 'yyyy-MM-dd') : undefined
    const endDate = dateRange?.to ? formatDate(dateRange.to, 'yyyy-MM-dd') : undefined
    updateHashParams(startDate, endDate, grouping || undefined)
  }, [dateRange, grouping])

  // Check if form is complete
  const isFormComplete = useMemo(
    () => Boolean(dateRange?.from && dateRange?.to && campaignId && grouping),
    [dateRange, campaignId, grouping]
  )

  // Handle form submission
  const handleGenerateReport = async () => {
    if (!campaignId || !dateRange?.from || !dateRange?.to || !grouping) {
      return
    }

    setError(null)

    try {
      const result = await generateReport.mutateAsync({
        campaignId,
        startDate: formatDate(dateRange.from, 'yyyy-MM-dd'),
        endDate: formatDate(dateRange.to, 'yyyy-MM-dd'),
        grouping: grouping as GroupingType,
      })
      setReport(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate report')
      setReport(null)
    }
  }

  const handleDateSelect = useCallback((range?: DateRange) => {
    setDateRange(range)
  }, [])

  const handleGroupingChange = useCallback((value: string) => {
    setGrouping(value as GroupingType)
  }, [])

  return (
    <div className="w-full h-full p-6 overflow-y-auto space-y-6">
      {/* Campaign Selector at the top */}
      <div className="flex items-center justify-between">
        <div className="flex-shrink-0">
          <h1 className="text-heading font-bold text-gray-900 mb-1">Custom Reports</h1>
          <p className="text-detail text-gray-600">
            Analyze call performance across different 'voices', 'experiments' and 'lists' groups
          </p>
        </div>
        <CampaignSelector
          campaigns={campaigns}
          selectedCampaignId={campaignId}
          onCampaignChange={setCampaignId}
          className="w-[400px]"
          disabled={campaignsLoading}
        />
      </div>

      <Card>
        <CardHeader></CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <label className="text-body font-medium w-24">Date Range:</label>
                <div className="flex-1">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-[300px] justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateRange?.from ? (
                          dateRange?.to ? (
                            <>
                              {formatDate(dateRange.from, 'LLL dd, y')} - {formatDate(dateRange.to, 'LLL dd, y')}
                            </>
                          ) : (
                            formatDate(dateRange.from, 'LLL dd, y')
                          )
                        ) : (
                          <span>Pick a date range</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="range"
                        defaultMonth={dateRange?.to}
                        selected={dateRange}
                        onSelect={handleDateSelect}
                        numberOfMonths={2}
                        disabled={(date) => isFuture(date)}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <label className="text-body font-medium w-24">Grouping:</label>
                <div className="flex-1">
                  <Select value={grouping} onValueChange={handleGroupingChange}>
                    <SelectTrigger className="w-[300px]">
                      <SelectValue placeholder="Select grouping" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="voice">Voice</SelectItem>
                      <SelectItem value="experiment">Experiment</SelectItem>
                      <SelectItem value="list">List</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-24"></div>
                <Button
                  onClick={handleGenerateReport}
                  disabled={generateReport.isPending || !isFormComplete}
                  className="w-full max-w-[200px]"
                >
                  {generateReport.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating Report...
                    </>
                  ) : (
                    'Generate Report'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Charts Section */}
      {report && <ReportsCharts report={report} />}

      {/* Error message */}
      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <Badge variant="destructive">Error</Badge>
              <span className="text-body text-destructive">{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table Section */}
      {report && <ReportsTable report={report} />}
    </div>
  )
}